import { IPagedCollection } from '@moko/utils';
import { internalFetch } from '../utils/fetchHelper';

export interface IReportTemplate {
  id: number;
  uuid: string;
  name: string;
  fileName: string;
  fileType: string;
  entityType: string | null;
  reportUrl: string;
  note: string | null;
  isDeleted: boolean;
  createdAt: string;
  modifiedAt: string;
  createdBy: string | number;
  modifiedBy: string | null;
  tenantId: string | null;
  tenantName: string | null;
}

export interface IUploadReportTemplateRequest {
  content: string;
  name: string;
  fileName: string;
  contentType: string;
  entityType: string;
}

export interface IUploadReportTemplateResponse {
  reportTemplateId: number;
}

/** Update: name is required; the remaining fields (incl. file content) are optional. */
export interface IUpdateReportTemplateRequest {
  name: string;
  content?: string;
  fileName?: string;
  contentType?: string;
  entityType?: string;
}

export interface IDuplicateReportTemplateRequest {
  tenantId?: number;
  name?: string;
}

export interface IDuplicateReportTemplateResponse {
  reportTemplateId: number;
  tenantId: number;
  isSuccess: boolean;
  message: string | null;
  correlationId: string;
}

export const getReportTemplates = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IPagedCollection<IReportTemplate>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/reportTemplates?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${search ? `&search=${search}` : ''}${
      filters ? `&filters=${encodeURIComponent(filters)}` : ''
    }&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};

export const uploadReportTemplate = async (
  abortController: AbortController,
  data: IUploadReportTemplateRequest
): Promise<IUploadReportTemplateResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/reportTemplate`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const updateReportTemplate = async (
  abortController: AbortController,
  reportTemplateId: number,
  data: IUpdateReportTemplateRequest
): Promise<void> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/reportTemplate?reportTemplateId=${reportTemplateId}`,
    'PUT',
    abortController,
    JSON.stringify(data)
  );
};

export const duplicateReportTemplate = async (
  abortController: AbortController,
  reportTemplateId: number,
  data: IDuplicateReportTemplateRequest
): Promise<IDuplicateReportTemplateResponse> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/reportTemplate/duplicate?reportTemplateId=${reportTemplateId}`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export const downloadReportTemplate = async (
  abortController: AbortController,
  reportTemplateId: number
): Promise<Response> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/downloadReportTemplate?reportTemplateId=${reportTemplateId}`,
    'GET',
    abortController,
    undefined,
    true
  );
};

/**
 * Returns a generated empty Word template (EmptyReportTemplate.docx) as
 * the raw `Response` so the caller can stream the bytes into a Blob and
 * trigger a download — same pattern as `downloadReportTemplate`.
 */
export const generateEmptyReportTemplate = async (
  abortController: AbortController,
  entityMetadataName?: string
): Promise<Response> => {
  const qs = new URLSearchParams();
  if (entityMetadataName) qs.set('entityMetadataName', entityMetadataName);
  qs.set('code', import.meta.env.VITE_BACKEND_API_KEY);
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/generateEmptyReportTemplate?${qs.toString()}`,
    'GET',
    abortController,
    undefined,
    true
  );
};

export const deleteReportTemplate = async (
  abortController: AbortController,
  reportTemplateId: number
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/reportTemplate?reportTemplateId=${reportTemplateId}`,
    'DELETE',
    abortController
  );
};
